import { Box, BoxProps, Typography } from '@mui/material';
import React from 'react';
import {
  H3,
  RequiredOptionTopTypography,
  StyledTable,
  TableArea,
} from '../../components/ui/StyledComponents';
import { StyledTableCell, TableBase } from '../../components/ui/TableBase';
import { JsonTableData, toData } from '../../features/Model';
import {
  createTableData,
  LinkData,
  RankingColumn,
  RankingColumnId,
  RankingRow,
  RankingTableData,
} from '../../features/ViewModel';
import { useI18n } from '../../hooks/I18n';
import { useData, useTableData } from '../../hooks/UseData';

import { TableBody, TableHead, TableRow, Theme } from '@mui/material';
import { AppLink } from '../../components/ui/AppLink';
import { formatOrLocalize } from '../../features/Formatter';
import { usePageTitleUpdateEffect } from '../../hooks/PageUtil';
import { TableInstance } from '../../lib/ReactTableWrapper';
import { defined, Optional, returnIf } from '../../utils/Util';

type TopPageTableProps = BoxProps & {
  path: string;
  link: LinkData;
  nameColumnId: RankingColumnId;
  targetColumnId: RankingColumnId;
  targetColumnWidth: string;
  messageForNotFound: string | undefined;
};

const createTable = (
  reactTable: TableInstance<RankingRow, RankingColumn>,
  targetColumnWidth: string,
  theme: Theme
): React.ReactElement => {
  const { headerGroups, prepareRow, page } = reactTable;
  const widths = ['60px', 'auto', targetColumnWidth];

  return (
    <StyledTable style={{ tableLayout: 'fixed' }}>
      <TableHead>
        {headerGroups.map((headerGroup) => (
          <TableRow key={headerGroup.getHeaderGroupProps()['key']}>
            {headerGroup.headers.map((column, index) => (
              <StyledTableCell
                key={column.id}
                header={true}
                theme={theme}
                sx={{ width: widths[index] }}
              >
                {column.render('Header')}
              </StyledTableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody>
        {page.map((row) => {
          prepareRow(row);
          return (
            <TableRow key={row.id}>
              {row.cells.map((cell) => {
                const key = cell.getCellProps()['key'];
                return (
                  <StyledTableCell key={key} theme={theme}>
                    {cell.render('Cell')}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </StyledTable>
  );
};

const TopPageTable: React.FC<TopPageTableProps> = ({
  path,
  link,
  nameColumnId,
  targetColumnId,
  targetColumnWidth,
  messageForNotFound,
  ...props
}) => {
  const i18n = useI18n();
  const { isLoading, data: tableData } = useTableData(
    path,
    undefined,
    (data: JsonTableData) => createTableData(toData(data))
  );

  if (!(tableData instanceof RankingTableData)) {
    return (
      <Typography variant="body2" sx={{ mx: 1 }}>
        {messageForNotFound ?? ''}
      </Typography>
    );
  }
  const nameColumn = tableData.getColumn(nameColumnId.getFullId());
  // 現役ボタンを非表示にするためactiveRankは常にfalse
  const rankingColumn = returnIf(
    tableData.getColumn(targetColumnId.getFullId()),
    (column) =>
      new RankingColumn(column.id, column.hasRank(), false, false, false, false)
  );
  if (!defined(nameColumn) || !defined(rankingColumn)) {
    return <></>;
  }
  let columns: RankingColumn[];
  let rows: RankingRow[];
  if (rankingColumn.hasRank()) {
    // ポイントランキング、通算ランキング
    columns = [nameColumn, rankingColumn];
    const getRank = (row: RankingRow): number => {
      const rank = row.getCell(rankingColumn.id)?.rank?.rank;
      return rank ?? Number.MAX_SAFE_INTEGER;
    };
    rows = tableData
      .getRows()
      .sort((row1, row2) => getRank(row1) - getRank(row2))
      .filter((row) => getRank(row) <= 10);
  } else {
    // 最新レース結果
    columns = [rankingColumn, nameColumn];
    rows = tableData.getRows().slice(0, 10);
  }

  const createTableWithTargetColumnWidth = (
    reactTable: TableInstance<RankingRow, RankingColumn>,
    theme: Theme
  ): React.ReactElement => createTable(reactTable, targetColumnWidth, theme);

  return (
    <>
      <TableBase
        tableData={new RankingTableData(columns, rows)}
        isLoading={isLoading}
        initialState={{}}
        showAggregatorButtons={false}
        createTable={createTableWithTargetColumnWidth}
        {...props}
      />
      <Typography
        variant="body2"
        component={'span'}
        sx={{ pl: 1, mr: 1, mt: 'auto' }}
      >
        <AppLink to={link.getLinkData()}>
          {i18n.LABELS.localize('top_read_more')}
        </AppLink>
      </Typography>
    </>
  );
};
const TopPageBox: React.FC<{
  topTableModel: TopTableModel;
  category?: Optional<string>;
}> = ({ topTableModel, category }) => {
  const path = topTableModel.getPath(category);
  const link = topTableModel.getLink(category);

  const categoryTypography =
    category !== undefined ? (
      <RequiredOptionTopTypography sx={{ ml: 1, mr: 'auto' }}>
        {category.toUpperCase()}
      </RequiredOptionTopTypography>
    ) : (
      <></>
    );

  return (
    // marginだと50%を超える
    <Box key={path} sx={{ width: { xs: '100%', sm: '50%' }, px: 1, mb: 1 }}>
      <H3 title={topTableModel.displayName} key={topTableModel.displayName}>
        <div style={{ display: 'flex' }}>{categoryTypography}</div>
        <TopPageTable
          path={path}
          link={link}
          nameColumnId={topTableModel.nameColumnId}
          targetColumnId={topTableModel.targetColumnId}
          targetColumnWidth={topTableModel.targetColumnWidth}
          messageForNotFound={topTableModel.messageForNotFound}
        />
      </H3>
    </Box>
  );
};

class TopTableModel {
  readonly targetColumnWidth;
  readonly messageForNotFound;
  constructor(
    readonly displayName: string,
    private readonly path: string,
    private readonly hash: string,
    private readonly queries: [string, string][],
    private readonly searchQueries: [string, string][],
    readonly nameColumnId: RankingColumnId,
    readonly targetColumnId: RankingColumnId,
    targetColumnWidth?: string,
    messageForNotFound?: string
  ) {
    this.targetColumnWidth = targetColumnWidth ?? '60px';
    this.messageForNotFound = messageForNotFound;
  }

  getPath(category?: Optional<string>): string {
    const path = this.path + '/' + this.hash;
    const queries = [...this.queries];
    if (category !== undefined) {
      queries.push(['race_category', category]);
    }
    return (
      [path]
        .concat(queries.map((q) => q[0]))
        .concat(queries.map((q) => q[1]))
        .join('-') + '.json'
    );
  }

  getLink(category?: Optional<string>): LinkData {
    const queries = [...this.searchQueries];
    if (category !== undefined) {
      queries.push(['race_category', category]);
    }
    const search = queries.map((query) => query[0] + '=' + query[1]).join('&');
    return new LinkData(this.path, search, this.hash);
  }
}

const TopPageMessage: React.FC = () => {
  const i18n = useI18n();
  return (
    <>
      <TableArea title={i18n.LABELS.localize('top_welcome')}>
        {i18n.LABELS.localize('top_welcome_message')
          .split('\n')
          .map((text) => (
            <Typography key={text} variant="body2" sx={{ px: 1 }}>
              {text}
            </Typography>
          ))}
      </TableArea>
    </>
  );
};

const TopPageSf: React.FC = () => {
  const i18n = useI18n();
  const { fetchRaceData } = useData();
  const raceData = fetchRaceData();
  const latestRaceId =
    Object.keys(raceData)
      .map((v) => parseInt(v))
      .sort((a, b) => a - b)
      .map((v) => String(v))
      .slice(-1)[0] ?? '';

  const latestRaceName = formatOrLocalize(
    'race_id',
    latestRaceId,
    i18n,
    raceData
  ).displayName;

  const latestYear = raceData[latestRaceId]?.year ?? '';

  const latestYearName = formatOrLocalize(
    'race_year',
    String(latestYear),
    i18n,
    raceData
  ).displayName;

  const topTablesModel: [string, TopTableModel[]][] = [
    [
      i18n.LABELS.localize('top_latest'),
      [
        new TopTableModel(
          i18n.LABELS.localize('top_latest_race', undefined, {
            race: latestRaceName,
          }),
          `race/race_id/${latestRaceId}`,
          'ranking',
          [['race_kind', 'R']],
          [],
          new RankingColumnId('driver_name'),
          new RankingColumnId('position'),
          undefined,
          i18n.LABELS.localize('top_race_not_found')
        ),
        new TopTableModel(
          i18n.LABELS.localize('top_latest_driver_point', undefined, {
            year: latestYearName,
          }),
          `year/race_year/${latestYear}`,
          'driver_ranking',
          [],
          [],
          new RankingColumnId('driver_name'),
          new RankingColumnId('total_driver_point')
        ),
      ],
    ],
    [
      i18n.LABELS.localize('top_ranking'),
      [
        new TopTableModel(
          i18n.LABELS.localize('top_driver_ranking_win'),
          `driver_name_ranking`,
          'table',
          [],
          [['column_id', 'win-value']],
          new RankingColumnId('driver_name'),
          new RankingColumnId('win_sum')
        ),
        new TopTableModel(
          i18n.LABELS.localize('top_driver_ranking_driver_point'),
          `driver_name_ranking`,
          'table',
          [],
          [['column_id', 'total_driver_point_sum-value']],
          new RankingColumnId('driver_name'),
          new RankingColumnId('total_driver_point_sum'),
          '120px'
        ),
      ],
    ],
  ];

  usePageTitleUpdateEffect(i18n.LABELS.localize('page_title_top'));

  return React.useMemo(
    () => (
      <Box
        sx={{
          display: 'flex', // 横幅はコンテンツに合わせる
          flexDirection: 'row', // 横方向に並べる
          flexWrap: 'wrap', // はみ出したら改行
          width: '100%', // 親の幅と合わせる
          maxWidth: '960px',
          m: 'auto', // 横のセンタリング
        }}
      >
        <TopPageMessage />
        {topTablesModel.map(([displayName, tables]) => (
          <TableArea title={displayName} key={displayName}>
            {tables.map((topTableModel, index) => (
              <TopPageBox key={index} topTableModel={topTableModel} />
            ))}
          </TableArea>
        ))}
      </Box>
    ),
    []
  );
};

export default TopPageSf;
